<script setup>
import {ref, onMounted, onUnmounted, computed} from 'vue';
import {useOddsmatcherStore} from '@/stores/OddsmatcherStore';
import BaseCheckboxDropdown from '@/components/forms/BaseCheckboxDropdown.vue';
import BaseTimeSlider from "@/components/forms/BaseTimeSlider.vue";
import BaseInputFloatingLabelNumber from "@/components/forms/BaseInputFloatingLabelNumber.vue";
import LoginRequiredModal from "@/components/modals/LoginRequiredModal.vue";
import PremiumRequiredModal from "@/components/modals/PremiumRequiredModal.vue";

const oddsmatcherStore = useOddsmatcherStore();


const props = defineProps({
  matcherType: {
    type: String,  // Define the type of the prop
    default: 'normal'  // Set the default value to 'normal'
  },
})


const sportsOptions = [
  {value: 'football', label: 'Football'},
  {value: 'horses', label: 'Horses'},
];


// Control the visibility of the filters on mobile view
const isFilterOpen = ref(false);

// Determine if the view is mobile
const isMobileView = ref(window.innerWidth <= 992);

const updateIsMobileView = () => {
  isMobileView.value = window.innerWidth <= 992;
};

onMounted(() => {
  window.addEventListener('resize', updateIsMobileView);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobileView);
});

// Reference to the filters container
const filtersContainer = ref(null);

const toggleFilters = () => {
  isFilterOpen.value = !isFilterOpen.value;
};


// Compute event options from the store's events
const eventOptions = computed(() => {
  // Get filtered events based on selectedSports
  let filteredEvents = oddsmatcherStore.events;

  // Filter events by selected sports
  if (oddsmatcherStore.filters.selectedSports && oddsmatcherStore.filters.selectedSports.length > 0) {
    filteredEvents = filteredEvents.filter(event =>
        oddsmatcherStore.filters.selectedSports.includes(event.sport)
    );
  }

  // Filter events by time range
  if (oddsmatcherStore.filters.timeRange) {
    const now = new Date();
    const timeLimit = new Date(now.getTime() + oddsmatcherStore.filters.timeRange * 60 * 60 * 1000);
    filteredEvents = filteredEvents.filter(event =>
        new Date(event.startDateTime) <= timeLimit
    );
  }

  // Map events to include sport in the label
  const eventOptions = filteredEvents.map(event => ({
    value: event.description,
    label: event.description,
    sport: event.sport,
  }));

  // Remove duplicates based on event description
  const uniqueEventOptions = [];
  const eventSet = new Set();
  eventOptions.forEach(option => {
    if (!eventSet.has(option.value)) {
      eventSet.add(option.value);
      uniqueEventOptions.push(option);
    }
  });

  // Sort the events first by sport, then alphabetically by event description
  uniqueEventOptions.sort((a, b) => {
    if (a.sport < b.sport) return -1;
    if (a.sport > b.sport) return 1;
    if (a.value.toLowerCase() < b.value.toLowerCase()) return -1;
    if (a.value.toLowerCase() > b.value.toLowerCase()) return 1;
    return 0;
  });

  return uniqueEventOptions;
});

const AUTH_LEVEL = {
  GUEST: 1,
  USER: 2,
  PREMIUM: 3,
};

const showLoginRequiredModal = ref(false);
const showPremiumRequiredModal = ref(false);

function createAuthorizedComputed(getter, setter, requiredLevel) {
  return computed({
    get: getter,
    set(value) {
      if (oddsmatcherStore.userAuthorization < requiredLevel) {
        if (oddsmatcherStore.userAuthorization <= AUTH_LEVEL.GUEST) {
          // Trigger login modal
          showLoginRequiredModal.value = true;
        } else if (oddsmatcherStore.userAuthorization === AUTH_LEVEL.USER) {
          // Trigger premium modal
          showPremiumRequiredModal.value = true;
        }
        // Reset the value to the previous state
        setter(getter());
      } else {
        // Assign the new value
        setter(value);
      }
    },
  });
}

const selectedSports = createAuthorizedComputed(
  () => oddsmatcherStore.filters.selectedSports,
  (value) => { oddsmatcherStore.filters.selectedSports = [...value]; },
  AUTH_LEVEL.PREMIUM
);

const selectedEvents = createAuthorizedComputed(
  () => oddsmatcherStore.filters.selectedEvents,
  (value) => { oddsmatcherStore.filters.selectedEvents = [...value]; },
  AUTH_LEVEL.PREMIUM
);

const selectedBookies = createAuthorizedComputed(
  () => oddsmatcherStore.filters.selectedBookies,
  (value) => { oddsmatcherStore.filters.selectedBookies = [...value]; },
  AUTH_LEVEL.USER
);

const minOdds = createAuthorizedComputed(
  () => oddsmatcherStore.filters.minOdds,
  (value) => { oddsmatcherStore.filters.minOdds = value.toString(); },
  AUTH_LEVEL.PREMIUM
);

const maxOdds = createAuthorizedComputed(
  () => oddsmatcherStore.filters.maxOdds,
  (value) => { oddsmatcherStore.filters.maxOdds = value.toString(); },
  AUTH_LEVEL.PREMIUM
);

const timeRange = createAuthorizedComputed(
  () => oddsmatcherStore.filters.timeRange,
  (value) => { oddsmatcherStore.filters.timeRange = Number(value); },
  AUTH_LEVEL.PREMIUM
);

const betType = createAuthorizedComputed(
  () => oddsmatcherStore.filters.betType,
  (value) => { oddsmatcherStore.filters.betType = value; },
  AUTH_LEVEL.GUEST
);

</script>

<template>

  <!-- Login Required Modal -->
  <LoginRequiredModal :show="showLoginRequiredModal" @close="showLoginRequiredModal = false" />

  <!-- Premium Required Modal -->
  <PremiumRequiredModal :show="showPremiumRequiredModal" @close="showPremiumRequiredModal = false" />

  <!-- Filter Toggle Button for Mobile -->
  <!-- Overlay when filters are active -->
  <div v-if="isFilterOpen" class="overlay" @click="toggleFilters"></div>

  <button
      v-if="isMobileView && !isFilterOpen"
      class="filter-button"
      @click="toggleFilters"
  >
    <i class="bi bi-funnel"></i> Filters
  </button>

  <!-- Filter Content -->
  <div
      v-if="!isMobileView || isFilterOpen"
      class="filters-container"
      ref="filtersContainer">

    <!-- Close Button for Mobile -->
    <button v-if="isMobileView" class="close-button" @click="toggleFilters">
      Close Filters
    </button>

    <!-- Sports Filters -->
    <BaseCheckboxDropdown
        label="Sports"
        :options="sportsOptions"
        v-model="selectedSports"
        class="lg-top-row-filter"
    />

    <!-- Event Filter -->
    <BaseCheckboxDropdown
        label="Events"
        :options="eventOptions"
        v-model="selectedEvents"
        class="lg-top-row-filter"
    />

    <!-- Bookies Filter -->
    <BaseCheckboxDropdown
        label="Bookies"
        :options="oddsmatcherStore.bookies"
        v-model="selectedBookies"
        class="lg-top-row-filter"
    />

    <!-- Min Odds -->
    <BaseInputFloatingLabelNumber
        :config="{ label: 'Min Odds', hasClearButton: false }"
        :allow-negative="true"
        v-model="minOdds"
        class="lg-top-row-filter lg-top-row-filter-sm"

    />

    <!-- Max Odds -->
    <BaseInputFloatingLabelNumber
        :config="{ label: 'Max Odds', hasClearButton: false }"
        :allow-negative="false"
        v-model="maxOdds"
        class="lg-top-row-filter lg-top-row-filter-sm"
    />

    <!-- TimeSlider component -->
    <BaseTimeSlider
        v-if="matcherType === 'normal'"
        v-model="timeRange"
        class="time-slider"/>


    <!-- Bet Type Buttons -->
    <div class="bet-type-buttons">
      <button
          :class="{ active: oddsmatcherStore.filters.betType === 'normal' }"
          @click="betType = 'normal'">
        Normal
      </button>

      <button
          :class="{ active: oddsmatcherStore.filters.betType === 'freebet' }"
          @click="betType = 'freebet'">
        Freebet
      </button>
    </div>
  </div>
</template>


<style scoped>
.filters-container {
  display: flex;
  padding: 16px;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 1rem

}

.time-slider {
  width: 70%;
  margin-block: 1rem;
}


/* Filter Toggle Button for Mobile */
.filter-button {
  position: fixed;
  top: 40%;
  left: 0;
  transform: translateY(-50%);
  z-index: 1000;
  background-color: #2f3e4f;
  color: #ffffff;
  border: none;
  width: 50px;
  height: 80px;
  border-radius: 0 1000px 1000px 0;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
}

.filter-button:hover {
  background-color: #33475b;
  opacity: 1;
}

/* Close Button for Mobile */
.close-button {
  background-color: var(--clr-primary-50, #2f3e4f);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  color: #ffffff;
  transition: background-color 0.2s ease;
  margin-bottom: 16px;
}

.close-button:hover {
  background-color: var(--clr-primary-100, #33475b);
}


.bet-type-buttons {
  display: flex;
  margin-top: auto;
}

.bet-type-buttons button {
  padding: 8px 16px;
  border: 1px solid #ccc;
  background-color: #f2f2f2;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.bet-type-buttons button.active {
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
}


/* Styles for larger screens */
@media (min-width: 992px) {
  .filters-container {
    width: 80%;
    margin-inline: auto;
    margin-top: 20px;
    padding: 20px;
    background-color: #ffffff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .bet-type-buttons {
    margin-left: auto;
  }


  .lg-top-row-filter {
    width: 23%;
    margin-block: auto;
  }

  .lg-top-row-filter-sm {
    width: 11%;
  }

}


/* Responsive Styles */
@media (max-width: 992px) {
  .filters-container {
    position: fixed;
    top: 72px;
    left: 10%;
    width: 90%;
    overflow-y: auto;
    background-color: #fff;
    z-index: 999;
    padding-block: 60px; /* Space for close button */
    border-radius: 8px;
    flex-direction: column;
  }

  /* Overlay Styles */
  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(5px);
    z-index: 998;
  }


  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .filter-button {
    /* Adjust position for better visibility */
    top: 50%;
  }
}


</style>
